import { z } from "zod";

const optionalString = z
  .union([z.string(), z.undefined(), z.any()])
  .transform((value) => (typeof value === "string" ? value : undefined));

const optionalNumber = z
  .union([z.number(), z.undefined(), z.any()])
  .transform((value) =>
    typeof value === "number" && Number.isFinite(value) ? value : undefined,
  );

/** Required non-empty trimmed string (trait `type` is always present in valid data). */
const traitTypeRequired = z.preprocess(
  (val) => (typeof val === "string" ? val.trim() : val),
  z.string().min(1),
);

/** Trimmed non-empty string, else undefined (no synthetic empty string). */
const traitNameOptional = z
  .union([z.string(), z.undefined(), z.null(), z.any()])
  .transform((value): string | undefined => {
    if (value === undefined || value === null) return undefined;
    if (typeof value !== "string") return undefined;
    const t = value.trim();
    return t.length > 0 ? t : undefined;
  });

/** Finite number; coerces numeric strings (e.g. YAML) so round-trip preserves values. */
const traitRepeatOnlyAfterOptional = z
  .union([z.undefined(), z.null(), z.number(), z.string(), z.any()])
  .transform((value): number | undefined => {
    if (value === undefined || value === null) return undefined;
    if (typeof value === "number") {
      return Number.isFinite(value) ? value : undefined;
    }
    if (typeof value === "string") {
      const n = Number(value.trim());
      return Number.isFinite(n) ? n : undefined;
    }
    return undefined;
  });

export const columnsSchema = z.object({
  type: z.string(),
  name: z.string(),
  precision: optionalNumber.optional(),
  rounding: optionalString.optional(),
  signSymbol: optionalString.optional(),
});

export const elementsSchema = z.object({
  type: z.string(),
  name: z.string(),
  precision: optionalNumber.optional(),
  rounding: optionalString.optional(),
  signSymbol: optionalString.optional(),
});

/** Element shape for `variables[].of` when `type` is `list` (YAML blueprint list typing). */
const variableListOfSchema = z.looseObject({
  name: z.string(),
  type: z.string(),
});

export const variablesSchema = z.object({
  type: z.string(),
  name: z.string(),
  precision: optionalNumber.optional(),
  rounding: optionalString.optional(),
  signSymbol: optionalString.optional(),
  columns: z.array(columnsSchema).optional(),
  elements: z.array(elementsSchema).optional(),
  of: variableListOfSchema.optional(),
});

export const traitsSchema = z.looseObject({
  type: traitTypeRequired,
  name: traitNameOptional.optional(),
  ids: z.array(z.unknown()).optional(),
  product: optionalString.optional(),
  repeatOnlyAfter: traitRepeatOnlyAfterOptional.optional(),
});

const traitsArrayTransform = z.array(z.unknown()).transform((arr) =>
  arr.flatMap((item) => {
    const parsed = traitsSchema.safeParse(item);
    return parsed.success ? [parsed.data] : [];
  }),
);

const variablesArrayTransform = z.array(z.unknown()).transform((arr) =>
  arr.flatMap((item) => {
    const parsed = variablesSchema.safeParse(item);
    return parsed.success ? [parsed.data] : [];
  }),
);

const metadataNormalizedSchema = z.object({
  description: z.string().optional(),
  traits: traitsArrayTransform.optional(),
  variables: variablesArrayTransform.optional(),
});

export type MetadataNormalized = z.infer<typeof metadataNormalizedSchema>;

const omitUndefinedMetadataKeys = (
  val: MetadataNormalized,
): MetadataNormalized =>
  Object.fromEntries(Object.entries(val).filter(([, v]) => v !== undefined));

export const metadataSchema = z
  .preprocess((raw: unknown) => {
    const base =
      raw != null && typeof raw === "object" && !Array.isArray(raw)
        ? (raw as Record<string, unknown>)
        : {};
    const out: Record<string, unknown> = {};

    if ("description" in base) {
      const d = base.description;
      if (typeof d === "string") {
        out.description = d;
      }
    }

    if ("traits" in base) {
      let { traits } = base;
      if (
        traits !== undefined &&
        traits !== null &&
        typeof traits === "object" &&
        !Array.isArray(traits)
      ) {
        traits = Object.values(traits as Record<string, unknown>);
      }
      out.traits = Array.isArray(traits) ? traits : [];
    }

    if ("variables" in base) {
      const { variables } = base;
      out.variables = Array.isArray(variables) ? variables : [];
    }

    return out;
  }, metadataNormalizedSchema)
  .transform(omitUndefinedMetadataKeys);

const ROW_OTHER_EXCLUDE = new Set([
  "name",
  "type",
  "columns",
  "elements",
  "of",
]);

export const ROW_OTHER_KEYS = Object.keys(variablesSchema.shape).filter(
  (k) => !ROW_OTHER_EXCLUDE.has(k),
) as (keyof z.infer<typeof variablesSchema>)[];

const COLUMN_ORDER: (keyof z.infer<typeof columnsSchema>)[] = [
  "name",
  "type",
  "precision",
  "rounding",
  "signSymbol",
];
export const COLUMN_KEYS = COLUMN_ORDER.filter(
  (k) => Object.prototype.hasOwnProperty.call(columnsSchema.shape, k) === true,
);

const ELEMENT_ORDER: (keyof z.infer<typeof elementsSchema>)[] = [
  "name",
  "type",
  "precision",
  "rounding",
  "signSymbol",
];
export const ELEMENT_KEYS = ELEMENT_ORDER.filter(
  (k) => Object.prototype.hasOwnProperty.call(elementsSchema.shape, k) === true,
);

export type Columns = z.infer<typeof columnsSchema>;
export type Elements = z.infer<typeof elementsSchema>;
export type Variables = z.infer<typeof variablesSchema>;
export type Traits = z.infer<typeof traitsSchema>;
export type Metadata = MetadataNormalized;
